import {
  Component,
  effect,
  inject,
  model,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { KENDO_DIALOGS } from '@progress/kendo-angular-dialog';
import { InputsModule, KENDO_INPUTS } from '@progress/kendo-angular-inputs';
import { searchIcon } from '@progress/kendo-svg-icons';
import { IconsModule } from '@progress/kendo-angular-icons';
import { FormsModule } from '@angular/forms';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import {
  KENDO_GRID,
  RowArgs,
  SelectionEvent,
} from '@progress/kendo-angular-grid';
import {
  ComboBoxComponent,
  DropDownsModule,
} from '@progress/kendo-angular-dropdowns';
import { Account } from './change-account-models';
import { AuthService } from '@services/auth/auth.service';

import { ApiService } from '@services/api/api.service';

import { LabelModule } from '@progress/kendo-angular-label';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { KENDO_LOADER } from '@progress/kendo-angular-indicators';
import { CommonService } from '@services/common/common.service';
import { AsyncPipe } from '@angular/common';

import { Subject, of, EMPTY, takeUntil, tap, Observable } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  catchError,
  finalize,
  map,
} from 'rxjs/operators';

@Component({
  //standalone: true,
  selector: 'app-change-account',
  imports: [
    KENDO_INPUTS,
    KENDO_DIALOGS,
    KENDO_BUTTON,
    InputsModule,
    IconsModule,
    FormsModule,
    KENDO_GRID,
    KENDO_LOADER,
    DropDownsModule,
    LabelModule,
    AsyncPipe,
  ],
  templateUrl: './change-account.component.html',
  styleUrls: ['./change-account.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ChangeAccountComponent {
  private readonly gridHeaderHeight = 42;
  private readonly gridRowHeight = 34;
  private readonly maxVisibleRows = 7;

  @ViewChild('combobox', { static: false })
  public combobox!: ComboBoxComponent;

  authService = inject(AuthService);
  apiService = inject(ApiService);
  commonService = inject(CommonService);
  destroy$ = this.commonService.onDestroy$();

  show = model(false);
  icons = { searchIcon };
  isSystemUser: boolean = false;
  //accounts$: Observable<Account[]> = new Observable<Account[]>;
  public accounts: Account[] = [];
  userData: any;
  selectedKeys: string[] = [];
  isDisabled: boolean = true;
  public loading: boolean = false;
  public isLoaderVisible: boolean = false;
  selectedAccount: Account = new Account();
  isLoadingSearch = false;

  // Emits each filter term
  private filter$ = new Subject<string>();

  // Stream of account results for the template
  accounts$ = this.filter$.pipe(
    debounceTime(500),
    distinctUntilChanged(),
    switchMap((term) => {
      // Minimum length guard
      if (!term || term.length < 1) {
        return of<Account[]>([]);
      }
      this.isLoadingSearch = true;
      return this.filterAccountsWithDelay(term).pipe(
        map((res) => res ?? []),
        catchError(() => of<Account[]>([])),
        finalize(() => {
          this.isLoadingSearch = false;
        }),
      );
    }),
  );

  onFilterChange(term: string) {
    this.filter$.next(term);
  }

  constructor() {
    this.authService.userData$.pipe(takeUntilDestroyed()).subscribe((x) => {
      if (x != null) {
        this.userData = x;
        this.initializeData();
      }
    });

    effect(() => {
      if (this.show()) {
        this.selectedAccount = new Account();

        if (this.selectedKeys.length == 0) {
          this.initializeData();
        } else {
          this.isDisabled =
            this.selectedKeys &&
            this.selectedKeys[0] == this.userData.selectedAccountId;
        }
      }
    });
  }

  protected initializeData() {
    if (this.userData && this.userData.accounts) {
      this.isSystemUser =
        this.userData.systemRole != null && this.userData.systemRole != ''
          ? true
          : false;

      if (this.userData.selectedAccountId != null) {
        this.selectedKeys = []; // Empty first
        this.selectedKeys.push(this.userData.selectedAccountId);
      }
    }
  }

  public getSerialColumnClass(): string {
    return this.isSystemUser ? 'gridCell' : 'lastCell';
  }

  public hasGridOverflow(): boolean {
    const totalRows = this.userData?.accounts?.length ?? 0;
    return totalRows > this.maxVisibleRows;
  }

  public getGridHeight(): number {
    // Cap grid height and let the grid content area scroll for long lists.
    return this.gridHeaderHeight + this.maxVisibleRows * this.gridRowHeight;
  }

  public onCancel(): void {
    this.onClose();
  }

  public async onConfirm(): Promise<void> {
    this.isLoaderVisible = true;
    await this.updateAccountSelection();
  }

  public onClose(): void {
    this.isDisabled = false;
    this.isLoaderVisible = false;
    this.show.update((x) => false);
  }

  protected onAccountSelect(args: SelectionEvent): void {
    // Check if no rows are selected
    if (!args.selectedRows || args.selectedRows.length == 0) {
      // If the selected account was deselected, reselect it.
      // no rows are selected.
      if (args.deselectedRows) {
        // reselect the selected account if it was deselected
        for (let i = 0; i < args.deselectedRows.length; i++) {
          // Reset the selectedKeys
          if (args.selectedRows) {
            this.selectedKeys = []; // Empty first
            this.selectedKeys.push(args.deselectedRows[0].dataItem.accountId);
          }
          this.isDisabled =
            this.selectedKeys &&
            this.selectedKeys[0] == this.userData.selectedAccountId;
          break;
        }
      }
    } else {
      args.selectedRows?.forEach((a) => {
        // Enable or Disable Apply Button
        this.isDisabled =
          this.userData.selectedAccountId == a.dataItem.accountId;
      });
    }
  }

  protected onSearchAccountSelect(value: any): void {
    if (value != null) {
      // This is an account that was selected from a system administrator
      this.isDisabled = this.userData.selectedAccountId == value.accountId;
      this.selectedAccount = value;
      // if this account appears in the list of accounts, select it in the grid.
      for (let i = 0; i < this.userData.accounts.length; i++) {
        if (this.userData.accounts[i].accountId == value.accountId) {
          this.selectedKeys = []; // Empty first
          this.selectedKeys.push(value.accountId);
          break;
        }
      }
    }
  }

  protected filterAccountsWithDelay(searchVal: string): Observable<any> {
    return this.apiService.getRequest(`/account/change/${searchVal}`);
  }

  protected async updateAccountSelection(): Promise<void> {
    let newSelectedId = this.selectedKeys[0];
    if (this.isSystemUser && this.selectedAccount.accountId != '') {
      // override grid selection with selected account in search
      newSelectedId = this.selectedAccount.accountId;
    }

    if (newSelectedId != null) {
      this.apiService
        .patchRequest(`/user/changeaccount/${newSelectedId}`, null)
        .subscribe(async (response: any) => {
          // This method returns true if the account was successfully set to the new selected account.
          // If the user is a system user and they select an account that they have no role in, it returns that account's data.

          if (this.isSystemUser && this.selectedAccount.accountId != '') {
            // Add the account to the user data for the system user if not already added.
            if (
              !this.userData.accounts.some(
                (x: any) => x.accountId == newSelectedId,
              )
            ) {
              // Update the local session data.
              this.userData.selectedAccountId = newSelectedId;
              response.impersonating = true;
              this.userData.accounts.push(response);
            } else {
              // User already has this account in their list.
              // Just update the selected account id.
              this.userData.selectedAccountId = newSelectedId;
            }

            // Update User Data Session
            await this.authService.updateUserDataSession(this.userData);
          } else {
            // if boolean true is returned, the account change was successful.
            if (response) {
              // Update the local session data.
              this.userData.selectedAccountId = newSelectedId;
              await this.authService.updateUserDataSession(this.userData);
            }
          }

          // Finally close the dialog
          this.onClose();
        });
    }
  }
}
